/**
 * TransactionBasicsSection - Transaction basic fields validation
 */

import { MapPin, Users } from 'lucide-react';
import { useValidationStore } from '../../store';
import { ValidationFieldRow } from './ValidationFieldRow';
import { ValidationSection } from './ValidationSection';

export function TransactionBasicsSection({
  extractedData,
}: {
  extractedData: {
    propertyAddress?: string;
    salePrice?: number;
    closingDate?: string;
    disbursementDate?: string;
    fileNumber?: string;
    loanId?: string;
    mic?: string;
    borrowerName?: string;
    borrowerAddress?: string;
    sellerName?: string;
    lenderName?: string;
    settlementAgentName?: string;
    parties?: Array<{
      name: string;
      party_type: string;
      email?: string;
      phone?: string;
      nmls?: string;
      license_id?: string;
    }>;
  };
}) {
  const { expandedSections, toggleSection } = useValidationStore();
  const isExpanded = expandedSections.has('transaction');
  const isPartiesExpanded = expandedSections.has('parties');

  const hasData =
    extractedData.propertyAddress ||
    extractedData.salePrice ||
    extractedData.closingDate ||
    extractedData.disbursementDate ||
    extractedData.fileNumber ||
    extractedData.loanId ||
    extractedData.mic;

  const hasParties =
    extractedData.borrowerName ||
    extractedData.borrowerAddress ||
    extractedData.sellerName ||
    extractedData.lenderName ||
    extractedData.settlementAgentName ||
    extractedData.parties?.length;

  if (!hasData && !hasParties) {
    return null;
  }

  return (
    <>
      {/* Transaction Information Section */}
      {hasData && (
        <ValidationSection
          title="Transaction Information"
          description="Property and transaction details"
          icon={<MapPin className="h-5 w-5 text-muted-foreground" />}
          isExpanded={isExpanded}
          onToggle={() => toggleSection('transaction')}
        >
          <div className="space-y-3">
            {extractedData.propertyAddress && (
              <ValidationFieldRow
                category="transaction"
                fieldKey="propertyAddress"
                label="Property Address"
                type="text"
                originalValue={extractedData.propertyAddress}
              />
            )}

            {extractedData.salePrice && (
              <ValidationFieldRow
                category="transaction"
                fieldKey="salePrice"
                label="Sale Price"
                type="currency"
                originalValue={extractedData.salePrice}
              />
            )}

            {extractedData.closingDate && (
              <ValidationFieldRow
                category="transaction"
                fieldKey="closingDate"
                label="Closing Date"
                type="date"
                originalValue={extractedData.closingDate}
              />
            )}

            {extractedData.disbursementDate && (
              <ValidationFieldRow
                category="transaction"
                fieldKey="disbursementDate"
                label="Disbursement Date"
                type="date"
                originalValue={extractedData.disbursementDate}
              />
            )}

            {extractedData.fileNumber && (
              <ValidationFieldRow
                category="transaction"
                fieldKey="fileNumber"
                label="File Number"
                type="text"
                originalValue={extractedData.fileNumber}
              />
            )}

            {extractedData.loanId && (
              <ValidationFieldRow
                category="transaction"
                fieldKey="loanId"
                label="Loan ID"
                type="text"
                originalValue={extractedData.loanId}
              />
            )}

            {extractedData.mic && (
              <ValidationFieldRow
                category="transaction"
                fieldKey="mic"
                label="MIC"
                type="text"
                originalValue={extractedData.mic}
              />
            )}
          </div>
        </ValidationSection>
      )}

      {/* Parties Section */}
      {hasParties && (
        <ValidationSection
          title="Parties"
          description="Borrower, seller, lender, and settlement agent information"
          icon={<Users className="h-5 w-5 text-muted-foreground" />}
          isExpanded={isPartiesExpanded}
          onToggle={() => toggleSection('parties')}
        >
          <div className="space-y-3">
            {extractedData.borrowerName && (
              <ValidationFieldRow
                category="parties"
                fieldKey="borrowerName"
                label="Borrower Name"
                type="text"
                originalValue={extractedData.borrowerName}
              />
            )}

            {extractedData.borrowerAddress && (
              <ValidationFieldRow
                category="parties"
                fieldKey="borrowerAddress"
                label="Borrower Address"
                type="text"
                originalValue={extractedData.borrowerAddress}
              />
            )}

            {extractedData.sellerName && (
              <ValidationFieldRow
                category="parties"
                fieldKey="sellerName"
                label="Seller Name"
                type="text"
                originalValue={extractedData.sellerName}
              />
            )}

            {extractedData.lenderName && (
              <ValidationFieldRow
                category="parties"
                fieldKey="lenderName"
                label="Lender Name"
                type="text"
                originalValue={extractedData.lenderName}
              />
            )}

            {extractedData.settlementAgentName && (
              <ValidationFieldRow
                category="parties"
                fieldKey="settlementAgentName"
                label="Settlement Agent"
                type="text"
                originalValue={extractedData.settlementAgentName}
              />
            )}

            {/* Display all extracted parties */}
            {extractedData.parties?.map((party, index) => (
              <div key={index} className="space-y-2 p-3 bg-muted/30 rounded-lg">
                <div className="text-sm font-medium text-muted-foreground capitalize">
                  {party.party_type.replace('_', ' ')}
                </div>
                <ValidationFieldRow
                  category="parties"
                  fieldKey={`party_${index}_name`}
                  label="Name"
                  type="text"
                  originalValue={party.name}
                />
                {party.email && (
                  <ValidationFieldRow
                    category="parties"
                    fieldKey={`party_${index}_email`}
                    label="Email"
                    type="text"
                    originalValue={party.email}
                  />
                )}
                {party.phone && (
                  <ValidationFieldRow
                    category="parties"
                    fieldKey={`party_${index}_phone`}
                    label="Phone"
                    type="text"
                    originalValue={party.phone}
                  />
                )}
                {party.nmls && (
                  <ValidationFieldRow
                    category="parties"
                    fieldKey={`party_${index}_nmls`}
                    label="NMLS ID"
                    type="text"
                    originalValue={party.nmls}
                  />
                )}
                {party.license_id && (
                  <ValidationFieldRow
                    category="parties"
                    fieldKey={`party_${index}_license`}
                    label="License ID"
                    type="text"
                    originalValue={party.license_id}
                  />
                )}
              </div>
            ))}
          </div>
        </ValidationSection>
      )}
    </>
  );
}
